import { pluralize } from './formatters';

/**
 * What the signed-in user may do with a survey. The API marks each survey with
 * `access`: 'owner' (their own, or any survey for an admin), or 'edit' / 'view'
 * for one shared with them. The role still applies on top: useAuth().can
 * combines the two, and the server enforces the same rules.
 */
const RANK = { view: 1, edit: 2, owner: 3 };

// The access each kind of permission needs: deleting is for owners (and admins).
const NEEDS = { view: 'view', create: 'edit', edit: 'edit', delete: 'owner' };

export const ACCESS_LABELS = { owner: 'Owner', edit: 'Can edit', view: 'Can view' };

// A survey created on this device and not synced yet has no `access`: it's the user's own.
export const surveyAccess = (survey) => survey?.access || 'owner';

export const surveyAllows = (survey, permission) => {
  const needed = NEEDS[String(permission).split('_')[0]] || 'owner';
  return RANK[surveyAccess(survey)] >= RANK[needed];
};

/** The access the user really has: an edit share is view-only for a role that can't edit. */
export const effectiveAccess = (survey, user) => {
  const access = surveyAccess(survey);
  const roleCanEdit = Boolean(user?.roles?.includes('admin') || user?.permissions?.includes('edit_survey'));
  return access === 'edit' && !roleCanEdit ? 'view' : access;
};

/** Whose survey this is, from the signed-in user's point of view (null if it's simply theirs). */
export const ownershipLabel = (survey, user) => {
  if (!survey) return null;
  const access = effectiveAccess(survey, user);
  if (access !== 'owner') return `Shared by ${survey.owner_name || 'another user'} · ${ACCESS_LABELS[access]}`;
  if (survey.creator_id && user?.id && survey.creator_id !== user.id) {
    return survey.owner_name ? `Owned by ${survey.owner_name}` : 'Owned by another user';
  }
  if (survey.share_count) return `Shared with ${pluralize(survey.share_count, 'person', 'people')}`;
  return null;
};
